import React, { useCallback, useMemo } from 'react';
import useEnsureCards from '../../hooks/useEnsureCards';
import useStableCallback from '../../hooks/useStableCallback';
import { createPrimaryDateResolver } from '../../utils/cards';
import { normalizeId } from '../../utils/ids';
import { canManage, canWriteCard } from '../../utils/permissions';
import { createLookupResolver } from '../../utils/lookups';
import useCollectionTags from '../../hooks/useCollectionTags';
import { useOptionalTags } from '../../contexts/TagsContext';
import { tileShowsTags } from '../cards/tileLayoutModel';
import { TILE_MAX_ROWS, readTileBody } from '../../config/tileSlotPolicy';
import { isLongTextType, pickDisplayFields } from './itemValueDisplay';
import { isLinkedRow, itemHomeCollectionId } from '../../utils/linkedRecords';
import { rowOwnCollection } from '../../utils/recordQualifier';
import ItemsListSkeleton from './ItemsListSkeleton';
import { ItemCard, photoFirst } from './ItemCard';

// The tile lives in ItemCard.js (KPR-1557); its old home still exports it.
export { ItemCard, photoFirst };

// No pool given: ONE empty pool, so the lookup resolver built over it (and
// every memoised tile handed it) keeps its identity between renders.
const NO_POOL = Object.freeze({});

/**
 * The frame every tile in a list takes, from the arrangements the list holds
 * (cardId -> stored layout): the deepest body's row count, capped at what a
 * body may hold, and whether a body of more than one row shows tags. One row
 * — every unarranged card — is the 162px frame and no class at all.
 */
export const tileFrame = (tileLayouts) => {
	let rows = 1;
	let tagLine = false;
	Object.values(tileLayouts || {}).forEach(layout => {
		const arranged = readTileBody(layout?.body)?.rows;
		const count = Math.min(Array.isArray(arranged) ? arranged.length : 0, TILE_MAX_ROWS);
		if (count < 2) return;
		rows = Math.max(rows, count);
		if (tileShowsTags(layout.body)) tagLine = true;
	});
	const classes = [];
	if (rows > 1) classes.push(`item-card-grid--rows-${rows}`);
	if (tagLine) classes.push('item-card-grid--tag-line');
	return { rows, tagLine, className: classes.join(' ') };
};

/**
 * The uniform card grid. Selection and the detail are the caller's: this
 * renders the given items (already sorted/grouped upstream) and reports
 * clicks through `onSelectItem`. With `expandedItemId` + `renderExpanded`
 * the selected card renders the detail inline (the phone layout).
 *
 * `readOnly` (the public shared view) renders the same cards with no
 * selection affordance and no actions at all.
 */
export default function ItemCardsGrid({
	items,
	cardsById,
	// cardPrimaryDate(id) from the card cache; optional.
	resolvePrimaryDate: resolvePrimaryDateOverride = null,
	itemsById = NO_POOL,
	collectionId,
	collection = null,
	getElementsForCard,
	loading = false,
	readOnly = false,
	emptyText = 'No items match the current filters.',
	selectedItemId = null,
	onSelectItem,
	expandedItemId = null,
	renderExpanded,
	onItemDeleted,
	// Overrides the pool-backed resolver built below. The public shared page
	// passes a title-map resolver here: it has no item pool to resolve
	// against (that fetch is authenticated), only the titles the server chose
	// to disclose.
	resolveLookupValue: resolveLookupValueOverride,
	// Measurement display: null = the viewer's preference; the public shared
	// page passes 'default' (the card's own rules).
	unitSystem: unitSystemOverride = null,
	// cardId -> the stored arrangement that applies to this reader, already
	// cascaded by the API (hooks/useTileLayouts). Absent for any card nobody
	// has arranged, and for the public shared page, which falls back to the
	// automatic pick.
	tileLayouts = null,
	// ---- multi-selection ----
	// A Set of selected ids, or null when this surface has no selection. The
	// state lives on the PAGE, not here: the grid is re-mounted per group and
	// a collapsed group's grid is unmounted entirely, so selection kept here
	// would vanish with a fold.
	selectedIds = null,
	onToggleSelect,
	onLongPressSelect,
	selectionMode = false,
	// The page's "is anything selected" (useItemSelection `active`, which
	// counts an all-matching selection that holds no ids). Without it, a
	// checked id in `selectedIds` says the same.
	selectionActive: selectionActiveProp = false,
	selectionDisabledFor,
	// The item as it stands after ⋯ Tag… on a tile (the page's feed).
	onItemUpdated,
	// A list that belongs to NO collection — the tag page (tags T5), whose
	// rows come from every collection the reader reaches: each tile's title
	// is qualified by its own collection, and a row's ⋯ offers what reading
	// allows (the list holds no collection to say who may write; the item's
	// own page does, and the API asks either way).
	crossCollection = false,
	// The public share page (T7): each tile's tags are only the ones the page
	// discloses (non-restricted collection tags, publishable item tags), read
	// by their served names; nothing is asked.
	publicTags = false,
}) {
	// An item of a card this session has not seen yet (added by someone
	// else since the page loaded) asks the cache to fetch it.
	useEnsureCards(items);
	const unitSystem = unitSystemOverride;
	// getElementsForCard (from CardDefinitionsContext) is already memoized
	// per card id, so it is safe to call per row.
	const resolveElements = useMemo(() => {
		if (typeof getElementsForCard !== 'function') return () => [];
		return cardId => getElementsForCard(cardId) || [];
	}, [getElementsForCard]);

	const pooledResolver = useMemo(() => createLookupResolver({
		itemsById,
		cardsById,
		getElementsForCard: resolveElements,
	}), [itemsById, cardsById, resolveElements]);
	const resolveLookupValue = resolveLookupValueOverride || pooledResolver;

	// Each card's primary date, through its parent chain (utils/cards.js) —
	// the page's resolver when it has one (the cache's, with every element
	// set folded in, so a set's date resolves), else one over these cards.
	const resolvePrimaryDate = useMemo(
		() => resolvePrimaryDateOverride || createPrimaryDateResolver(cardsById || {}),
		[resolvePrimaryDateOverride, cardsById]
	);

	// The names every tile's tags read in, asked for once for the grid: the
	// collection's vocabulary and my private tags (none on the public page).
	const { tags: tagVocabulary } = useCollectionTags(collectionId, { enabled: !readOnly && !publicTags });
	const privateTags = useOptionalTags()?.tags || null;
	const tagContext = useMemo(
		() => (publicTags ? { public: true, cardsById } : { vocabulary: tagVocabulary, privateTags }),
		[publicTags, cardsById, tagVocabulary, privateTags]
	);

	const selectionActive = Boolean(selectedIds) && (selectionActiveProp || selectionMode || selectedIds.size > 0);

	// ---- Every prop a tile gets keeps its identity between renders ----
	// ItemCard is memoised (KPR-1557), so a checkbox toggle re-renders the
	// one tile whose `checked` changed, not the whole list. The page's
	// handlers are called through stable wrappers: some of them are rebuilt
	// on every selection change (useStableCallback always calls the latest).
	const onToggleCheck = useStableCallback(onToggleSelect);
	const onLongPress = useStableCallback(onLongPressSelect);
	const onSelect = useStableCallback(onSelectItem);
	const onDeleted = useStableCallback(onItemDeleted);
	const onTileUpdated = useStableCallback(onItemUpdated);
	const onClose = useCallback(() => onSelect?.(null), [onSelect]);
	// What a tile reads from its CARD, worked out once per card and handed
	// back as the same objects on every render until an input changes.
	const cardFacts = useMemo(() => {
		const byCard = new Map();
		return (cardId) => {
			if (byCard.has(cardId)) return byCard.get(cardId);
			const facts = {
				card: cardsById?.[cardId] || null,
				elementDefs: resolveElements(cardId),
				primaryDate: resolvePrimaryDate(cardId),
				tileLayout: tileLayouts?.[cardId] || null,
				canEdit: crossCollection ? false : (collection ? canWriteCard(collection, cardId) : true),
				canManage: crossCollection ? false : Boolean(collection && canManage(collection)),
			};
			byCard.set(cardId, facts);
			return facts;
		};
	}, [cardsById, resolveElements, resolvePrimaryDate, tileLayouts, collection, crossCollection]);
	// The tag page qualifies each title with its own collection, which
	// rowOwnCollection builds afresh on every call: one per item, kept.
	const ownCollections = useMemo(() => {
		if (!crossCollection) return null;
		const byItem = new Map();
		items.forEach(item => byItem.set(item, rowOwnCollection(item)));
		return byItem;
	}, [items, crossCollection]);

	// Every arranged row reads label over value, so a body of two or three
	// rows needs a taller frame than the one-row body. The frame is the
	// LIST's, not the tile's: the deepest arrangement here sets it for every
	// tile, so a grid (and every group's grid on the page, which share these
	// layouts) stays one height. A deep arrangement that shows tags also keeps
	// their line clear of its last row.
	const frame = useMemo(() => tileFrame(tileLayouts), [tileLayouts]);

	const showSkeletons = loading && items.length === 0;

	// First load, nothing to show: the shared tile skeletons, in this grid's own frame.
	if (showSkeletons) return <ItemsListSkeleton variant="tiles" className={frame.className} />;

	return (
		<div className={['item-card-grid', frame.className].filter(Boolean).join(' ')}>
			{items.map(item => {
						const cardId = normalizeId(item?.card_id);
						const facts = cardFacts(cardId);
						const id = normalizeId(item?._id);
						const linked = isLinkedRow(item);
						const disabledReason = selectedIds && typeof selectionDisabledFor === 'function'
							? selectionDisabledFor(item)
							: null;
						return (
							<ItemCard
								key={item?._id}
								selectable={Boolean(selectedIds) && !readOnly}
								checked={Boolean(selectedIds?.has(id))}
								checkable={!disabledReason}
								checkDisabledReason={disabledReason}
								onToggleCheck={onToggleCheck}
								onLongPress={onLongPress}
								selectionMode={selectionMode}
								selectionActive={selectionActive}
								item={item}
								card={facts.card}
								elementDefs={facts.elementDefs}
								resolveLookupValue={resolveLookupValue}
								unitSystem={unitSystem}
								primaryDate={facts.primaryDate}
								tileLayout={facts.tileLayout}
								readOnly={readOnly}
								selected={Boolean(selectedItemId) && id === selectedItemId}
								onSelect={onSelect}
								expanded={Boolean(expandedItemId) && id === expandedItemId}
								renderExpanded={renderExpanded}
								// A record a collection link brought in lives in its own
								// collection: it opens there and is read-only here.
								collection={linked ? null : collection}
								collectionId={itemHomeCollectionId(item, collectionId)}
								canEdit={linked ? false : facts.canEdit}
								canManage={linked ? false : facts.canManage}
								cardsById={cardsById}
								onDeleted={onDeleted}
								onClose={onClose}
								tagContext={tagContext}
								onItemUpdated={onTileUpdated}
								ownCollection={ownCollections ? ownCollections.get(item) : null}
							/>
						);
					})
				}
			{items.length === 0 && emptyText ? (
				<div className="item-card-grid__empty">
					{emptyText}
				</div>
			) : null}
		</div>
	);
}

export { pickDisplayFields, isLongTextType };
